import { parseQuickAdd } from "@founderdeck/core";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useApp } from "../app/AppContext";
import type { Note } from "../lib/api";
import { todayKey } from "../lib/date";

function jotTime(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/**
 * A single jot with its full action set (edit, → task, → note, star, delete).
 * Shared by the home widget and the history modal so both behave identically.
 * `onLeave` lets a container (e.g. the modal) close itself when an action
 * navigates away.
 */
export function JotRow({ jot, onLeave }: { jot: Note; onLeave?: () => void }) {
  const app = useApp();
  const navigate = useNavigate();
  const today = todayKey();
  const [active, setActive] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(jot.title);

  function saveEdit() {
    const t = editText.trim();
    if (t && t !== jot.title) app.updateNote(jot.id, { title: t });
    setEditing(false);
  }

  function toTask() {
    const parsed = parseQuickAdd(jot.title, { today, projects: app.projects });
    app.createTask({
      title: parsed.title || jot.title,
      pri: parsed.pri ?? 2,
      estMin: parsed.estMin ?? 30,
      energy: parsed.energy ?? null,
      doDate: parsed.doDate ?? null,
      fromHour: parsed.fromHour ?? null,
      deadline: parsed.deadline ?? null,
      mit: parsed.mit ?? false,
      projectId: parsed.projectId ?? null,
    });
    app.deleteNote(jot.id);
    app.toast("Jot → task");
    setActive(false);
  }

  function toNote() {
    app.updateNote(jot.id, { dayDate: null });
    app.toast("Jot → note");
    onLeave?.();
    navigate({ to: "/app/notes", search: { note: jot.id } });
  }

  if (editing) {
    return (
      <div className="jot editing">
        <input
          className="jot-edit"
          value={editText}
          autoFocus
          onChange={(e) => setEditText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") saveEdit();
            if (e.key === "Escape") setEditing(false);
          }}
        />
        <div className="jot-actions">
          <button className="jbtn task" onClick={saveEdit}>
            Save
          </button>
          <button className="jbtn ghost" onClick={() => setEditing(false)}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="jot">
      <button className="jot-row" onClick={() => setActive(!active)}>
        <span className={`jot-time${jot.pinned ? " star" : ""}`}>
          {jot.pinned ? "★" : ""}
          {jotTime(jot.createdAt)}
        </span>
        <span className="jot-text">{jot.title || "…"}</span>
      </button>
      {active && (
        <div className="jot-actions">
          <button className="jbtn task" onClick={toTask}>
            ✓ Task
          </button>
          <button className="jbtn note" onClick={toNote}>
            ✎ Note
          </button>
          <button
            className="jbtn ghost"
            onClick={() => app.updateNote(jot.id, { pinned: !jot.pinned })}
            title={jot.pinned ? "Unstar" : "Star"}
          >
            ★
          </button>
          <button
            className="jbtn ghost"
            onClick={() => {
              setEditText(jot.title);
              setEditing(true);
              setActive(false);
            }}
            title="Edit"
          >
            ✏
          </button>
          <button className="jbtn ghost del" onClick={() => app.deleteNote(jot.id)} title="Delete">
            ✕
          </button>
        </div>
      )}
    </div>
  );
}
